import {
  type Decimal,
  isZeroDecimal,
  toScaled,
} from "@/lib/tool-kit/arithmetic/decimal";
import { BIG_TEN, BIG_ZERO, gcdBig } from "@/lib/tool-kit/math/bigint";

/**
 * Long division, digit by digit, the way it is taught.
 *
 * Both numbers are scaled by the same power of ten until the divisor is a whole number
 * ("move the decimal point in both"), then each digit of the dividend is brought down in
 * turn: current = remainder × 10 + digit, quotient digit = ⌊current ÷ divisor⌋, and the
 * new remainder is what is left. Nothing here touches a floating-point number.
 */
export interface DivisionStep {
  /** Index of the dividend digit brought down at this step. */
  index: number;
  digit: number;
  /** The number being divided: the previous remainder with this digit brought down. */
  current: bigint;
  quotientDigit: number;
  product: bigint;
  remainder: bigint;
  /** True for a zero appended after the dividend ran out of digits. */
  appended: boolean;
}

export type DivisionMode = "remainder" | "decimal";

export interface LongDivision {
  negative: boolean;
  mode: DivisionMode;
  /** The divisor after scaling (a whole number). */
  divisor: bigint;
  /** How many places the decimal point was moved in both numbers. */
  shift: number;
  /** Every dividend digit, appended zeros included, with no point. */
  digits: string;
  /** Digits before the decimal point in `digits`. */
  pointAt: number;
  /** Digits in the scaled dividend before any zeros were appended. */
  originalLength: number;
  /** True when the dividend row and quotient show a decimal point. */
  showPoint: boolean;
  steps: DivisionStep[];
  /** One quotient digit per step, leading zeros kept. */
  quotientDigits: string;
  /** Integer part of the quotient, no leading zeros ("0" if none). */
  quotientInteger: string;
  /** Fractional digits worked out (empty in remainder mode). */
  quotientFraction: string;
  remainder: bigint;
  /** The quotient is exact: the remainder reached zero. */
  exact: boolean;
  /** Index into `quotientFraction` where an endlessly repeating block starts. */
  repeatStart: number | null;
  /** The input had decimals but remainder mode was requested. */
  fellBackToDecimal: boolean;
}

export type DivisionError = "divide-by-zero" | "too-long";

export interface DivisionOptions {
  mode: DivisionMode;
  /** Fractional quotient digits to work out in decimal mode (0–30). */
  places: number;
}

const LEADING_ZEROS = /^0+(?=\d)/;
const TRAILING_ZEROS = /0+$/;
const NONZERO_DIGIT = /[1-9]/;

export const MAX_DIVISION_DIGITS = 40;
export const MAX_DIVISION_PLACES = 30;

function stepFor(
  index: number,
  digit: number,
  remainder: bigint,
  divisor: bigint,
  appended: boolean
): DivisionStep {
  const current = remainder * BIG_TEN + BigInt(digit);
  const quotient = current / divisor;
  return {
    index,
    digit,
    current,
    quotientDigit: Number(quotient),
    product: quotient * divisor,
    remainder: current % divisor,
    appended,
  };
}

interface Scaled {
  digits: string;
  pointAt: number;
  divisor: bigint;
  shift: number;
  fractionDigits: number;
}

function scaleOperands(dividend: Decimal, divisor: Decimal): Scaled {
  const divisorScaled = toScaled(divisor);
  const shift = divisorScaled.scale;
  let digits = dividend.int + dividend.frac;
  let fractionDigits = dividend.frac.length - shift;
  if (fractionDigits < 0) {
    digits += "0".repeat(-fractionDigits);
    fractionDigits = 0;
  }
  return {
    digits,
    pointAt: digits.length - fractionDigits,
    divisor: divisorScaled.value,
    shift,
    fractionDigits,
  };
}

function workThroughDigits(
  digits: string,
  divisor: bigint
): { steps: DivisionStep[]; remainder: bigint } {
  const steps: DivisionStep[] = [];
  let remainder = BIG_ZERO;
  for (let index = 0; index < digits.length; index++) {
    const step = stepFor(
      index,
      Number(digits[index]),
      remainder,
      divisor,
      false
    );
    steps.push(step);
    remainder = step.remainder;
  }
  return { steps, remainder };
}

function extendWithZeros(
  steps: DivisionStep[],
  startRemainder: bigint,
  divisor: bigint,
  fractionSoFar: number,
  places: number
): { remainder: bigint; repeatQuotientIndex: number | null } {
  let remainder = startRemainder;
  const seen = new Map<bigint, number>();
  let fraction = fractionSoFar;
  let repeatQuotientIndex: number | null = null;
  if (remainder !== BIG_ZERO) {
    seen.set(remainder, steps.length);
  }
  while (remainder !== BIG_ZERO && fraction < places) {
    const step = stepFor(steps.length, 0, remainder, divisor, true);
    steps.push(step);
    remainder = step.remainder;
    fraction += 1;
    if (remainder === BIG_ZERO) {
      break;
    }
    const earlier = seen.get(remainder);
    if (earlier !== undefined) {
      repeatQuotientIndex = earlier;
      break;
    }
    seen.set(remainder, steps.length);
  }
  return { remainder, repeatQuotientIndex };
}

/**
 * Divide, keeping every step. Signs are set aside and the result gets the usual sign
 * (negative when exactly one number is negative).
 */
export function longDivide(
  dividend: Decimal,
  divisor: Decimal,
  options: DivisionOptions
): LongDivision | { error: DivisionError } {
  if (isZeroDecimal(divisor)) {
    return { error: "divide-by-zero" };
  }
  if (
    dividend.int.length + dividend.frac.length > MAX_DIVISION_DIGITS ||
    divisor.int.length + divisor.frac.length > MAX_DIVISION_DIGITS
  ) {
    return { error: "too-long" };
  }
  const scaled = scaleOperands(dividend, divisor);
  const hasDecimals = scaled.fractionDigits > 0;
  const fellBackToDecimal = options.mode === "remainder" && hasDecimals;
  const mode: DivisionMode = fellBackToDecimal ? "decimal" : options.mode;

  const first = workThroughDigits(scaled.digits, scaled.divisor);
  const steps = first.steps;
  let remainder = first.remainder;
  let repeatQuotientIndex: number | null = null;
  if (mode === "decimal") {
    const places = Math.min(options.places, MAX_DIVISION_PLACES);
    const extended = extendWithZeros(
      steps,
      remainder,
      scaled.divisor,
      scaled.fractionDigits,
      places
    );
    remainder = extended.remainder;
    repeatQuotientIndex = extended.repeatQuotientIndex;
  }

  const digits =
    scaled.digits + "0".repeat(steps.length - scaled.digits.length);
  const quotientDigits = steps.map((s) => s.quotientDigit).join("");
  const rawInteger = quotientDigits.slice(0, scaled.pointAt);
  const quotientInteger = rawInteger.replace(LEADING_ZEROS, "") || "0";
  const quotientFraction = quotientDigits.slice(scaled.pointAt);
  const negative =
    dividend.negative !== divisor.negative &&
    (quotientInteger !== "0" ||
      NONZERO_DIGIT.test(quotientFraction) ||
      remainder !== BIG_ZERO);

  return {
    negative,
    mode,
    divisor: scaled.divisor,
    shift: scaled.shift,
    digits,
    pointAt: scaled.pointAt,
    originalLength: scaled.digits.length,
    showPoint: digits.length > scaled.pointAt,
    steps,
    quotientDigits,
    quotientInteger,
    quotientFraction,
    remainder,
    exact: remainder === BIG_ZERO,
    repeatStart:
      repeatQuotientIndex === null
        ? null
        : repeatQuotientIndex - scaled.pointAt,
    fellBackToDecimal,
  };
}

/** The dividend as it reads after the point was moved: "750", "0.5". */
export function scaledDividendText(division: LongDivision): string {
  const digits = division.digits.slice(0, division.originalLength);
  if (division.pointAt >= digits.length) {
    return digits.replace(LEADING_ZEROS, "");
  }
  return `${digits.slice(0, division.pointAt)}.${digits.slice(division.pointAt)}`;
}

/* ----------------------------------------------------------------- layout */

export type DivisionLayout = "bracket" | "european";

/** Two characters per digit: the digit, then a gap that holds the decimal point. */
function blankLine(digitCount: number): string[] {
  return new Array<string>(Math.max(0, digitCount * 2 - 1)).fill(" ");
}

function put(line: string[], endIndex: number, text: string): void {
  const start = endIndex - text.length + 1;
  for (let k = 0; k < text.length; k++) {
    const position = (start + k) * 2;
    if (position >= 0 && position < line.length) {
      line[position] = text[k];
    }
  }
}

function underline(line: string[], endIndex: number, length: number): void {
  const start = (endIndex - length + 1) * 2;
  const end = endIndex * 2;
  for (let position = Math.max(0, start); position <= end; position++) {
    if (position < line.length) {
      line[position] = "─";
    }
  }
}

function firstNonZeroIndex(digits: string): number {
  for (let i = 0; i < digits.length; i++) {
    if (digits[i] !== "0") {
      return i;
    }
  }
  return digits.length;
}

function gridWithPoint(division: LongDivision, chars: string[]): string[] {
  const line = [...chars];
  if (division.showPoint && division.pointAt > 0) {
    line[division.pointAt * 2 - 1] = ".";
  }
  return line;
}

function dividendRow(division: LongDivision): string[] {
  const line = blankLine(division.digits.length);
  put(line, division.digits.length - 1, division.digits);
  return gridWithPoint(division, line);
}

function quotientRow(division: LongDivision): string[] {
  const line = blankLine(division.digits.length);
  const lastIntegerIndex = division.showPoint
    ? division.pointAt - 1
    : division.digits.length - 1;
  const start = Math.min(
    firstNonZeroIndex(division.quotientDigits),
    Math.max(0, lastIntegerIndex)
  );
  for (let i = start; i < division.quotientDigits.length; i++) {
    line[i * 2] = division.quotientDigits[i];
  }
  return gridWithPoint(division, line);
}

/** The subtraction lines under the dividend. */
function workingRows(division: LongDivision): string[][] {
  const rows: string[][] = [];
  const first = division.steps.findIndex((s) => s.quotientDigit > 0);
  if (first === -1) {
    return rows;
  }
  const count = division.digits.length;
  let last = first;
  for (let i = first; i < division.steps.length; i++) {
    if (division.steps[i].current > BIG_ZERO) {
      last = i;
    }
  }
  for (let i = first; i <= last; i++) {
    const step = division.steps[i];
    const product = step.product.toString();
    const productLine = blankLine(count);
    put(productLine, step.index, product);
    rows.push(productLine);
    const rule = blankLine(count);
    underline(
      rule,
      step.index,
      Math.max(product.length, step.current.toString().length)
    );
    rows.push(rule);
    const next = division.steps[i + 1];
    const rest = blankLine(count);
    if (next && i < last) {
      put(rest, next.index, next.current.toString());
    } else {
      put(rest, step.index, step.remainder.toString());
    }
    rows.push(rest);
  }
  return rows;
}

/** The final answer as plain text: "87.5", "12 R 3". */
export function quotientText(division: LongDivision): string {
  const sign = division.negative ? "−" : "";
  if (division.mode === "remainder") {
    return `${sign}${division.quotientInteger}`;
  }
  const fraction = division.quotientFraction.replace(TRAILING_ZEROS, "");
  return `${sign}${division.quotientInteger}${fraction === "" ? "" : `.${fraction}`}`;
}

/**
 * The long-division diagram as lines of monospace text.
 *
 * `bracket` is the US/UK layout — divisor outside an "L"-shaped bar, quotient above the
 * dividend. `european` is the layout used across much of continental Europe and Latin
 * America: `dividend : divisor = quotient` on one line with the subtractions beneath.
 */
export function layoutDivision(
  division: LongDivision,
  layout: DivisionLayout
): string[] {
  const dividend = dividendRow(division);
  const working = workingRows(division).map((row) => row.join("").trimEnd());
  const divisorText = division.divisor.toString();
  if (layout === "european") {
    const quotient = quotientText(division);
    const head = `${dividend.join("").trimEnd()}  :  ${divisorText}  =  ${quotient}`;
    return [head, ...working];
  }
  const gutter = divisorText.length + 3;
  const pad = " ".repeat(gutter);
  const width = dividend.length + 2;
  return [
    pad + quotientRow(division).join("").trimEnd(),
    `${" ".repeat(divisorText.length + 1)}┌${"─".repeat(width)}`,
    `${divisorText} │ ${dividend.join("").trimEnd()}`,
    ...working.map((row) => pad + row),
  ];
}

/** The exact value of dividend ÷ divisor as a reduced fraction (signed numerator). */
export function divisionFraction(
  dividend: Decimal,
  divisor: Decimal
): { num: bigint; den: bigint } {
  const a = toScaled(dividend);
  const b = toScaled(divisor);
  const scale = Math.max(a.scale, b.scale);
  const top = a.value * BIG_TEN ** BigInt(scale - a.scale);
  const bottom = b.value * BIG_TEN ** BigInt(scale - b.scale);
  const divisorOfBoth = gcdBig(top, bottom);
  const negative = dividend.negative !== divisor.negative && top !== BIG_ZERO;
  const num = top / divisorOfBoth;
  return { num: negative ? -num : num, den: bottom / divisorOfBoth };
}
